/**
 * Widget Factory — Cria widgets com SEO + Analytics embutidos
 *
 * Uso:
 *   const widget = createWidget({
 *     id: 'busca-passagens',
 *     name: 'Busca de Passagens Aéreas',
 *     description: 'Encontre voos para Curitiba, porta de entrada da Ilha do Mel',
 *     provider: 'Vai de Promo',
 *     affiliateId: '14139',
 *     trackCategory: 'affiliate_click',
 *   });
 *
 *   // No componente:
 *   widget.trackClick({ origem: 'GRU', destino: 'CWB' });
 *   widget.getSchema('https://...');
 */

import { track, Events } from './analytics';
import { createWidgetProductSchema, type WidgetAffiliateConfig } from './seo-utils';

export interface WidgetConfig {
  /** Identificador único (snake_case) */
  id: string;
  /** Nome human-readable do widget */
  name: string;
  /** Descrição curta para SEO */
  description: string;
  /** Nome do parceiro/afiliado */
  provider: string;
  /** ID do afiliado (se houver) */
  affiliateId?: string;
  /** Categoria do evento GA4 */
  trackCategory: string;
  /** Preço estimado (opcional, para schema) */
  priceRange?: { min: number; max: number; currency: string };
}

export interface WidgetClickData {
  [key: string]: string | number | boolean;
}

export function createWidget(config: WidgetConfig) {
  const { id, name, description, provider, affiliateId, trackCategory, priceRange } = config;

  /**
   * Tracking de clique no widget.
   * Envia evento GA4 automaticamente.
   */
  function trackClick(data?: WidgetClickData) {
    track(
      Events.widgetInteract({
        widget_type: id as any,
        action: 'click',
        ...data,
      })
    );
  }

  /**
   * Schema.org Product/Offer para o widget.
   * A página que hospeda o widget deve incluir no JSON-LD.
   */
  function getSchema(offerUrl: string): ReturnType<typeof createWidgetProductSchema> {
    const schemaConfig: WidgetAffiliateConfig = {
      name,
      description,
      url: offerUrl,
      provider,
      ...(priceRange && { price: priceRange }),
    };
    return createWidgetProductSchema(schemaConfig);
  }

  /**
   * Keywords auxiliares para a página que hospeda o widget.
   */
  function getKeywords(baseKeywords: string): string {
    const widgetKw = `${name.toLowerCase()}, ${provider.toLowerCase()}, afiliado ${provider.toLowerCase()}`;
    return `${baseKeywords}, ${widgetKw}`;
  }

  /**
   * Data attributes para o container do widget.
   * Permite rastrear impressões via IntersectionObserver futuramente.
   */
  function getDataAttrs() {
    return {
      'data-widget-id': id,
      'data-widget-provider': provider,
      'data-widget-affiliate': affiliateId ?? '',
    };
  }

  return {
    id,
    name,
    description,
    provider,
    affiliateId,
    trackClick,
    getSchema,
    getKeywords,
    getDataAttrs,
  };
}

/* ─── Widgets pré-configurados ─── */

export const buscaPassagensWidget = createWidget({
  id: 'busca-passagens',
  name: 'Busca de Passagens Aéreas para Curitiba',
  description:
    'Encontre voos para Curitiba (CWB), aeroporto mais próximo da Ilha do Mel, com preços de agregadores de passagens.',
  provider: 'Vai de Promo',
  affiliateId: '14139',
  trackCategory: 'affiliate_click',
  priceRange: { min: 150, max: 1200, currency: 'BRL' },
});

export const ondeFicarWidget = createWidget({
  id: 'onde-ficar',
  name: 'Busca de Hospedagem na Ilha do Mel',
  description:
    'Compare pousadas em Nova Brasília e Encantadas, com reserva direta via WhatsApp e link para Booking.com.',
  provider: 'Booking',
  affiliateId: 'booking',
  trackCategory: 'affiliate_click',
  priceRange: { min: 80, max: 500, currency: 'BRL' },
});

export const previsaoTempoWidget = createWidget({
  id: 'previsao-tempo',
  name: 'Previsão do Tempo na Ilha do Mel',
  description:
    'Consulte a previsão do tempo, temperatura, maré e vento para planejar sua viagem à Ilha do Mel.',
  provider: 'Climatempo',
  trackCategory: 'utility_click',
});

export const mareWidget = createWidget({
  id: 'mare',
  name: 'Calendário de Maré da Ilha do Mel',
  description:
    'Consulte a previsão de maré para planejar seu dia de praia ou trilha na Ilha do Mel.',
  provider: 'Marinha do Brasil (DHN)',
  trackCategory: 'utility_click',
});

export const feriadosWidget = createWidget({
  id: 'feriados',
  name: 'Feriados Próximos com Previsão do Tempo',
  description:
    'Confira os próximos feriados nacionais, estaduais e municipais de Paranaguá com previsão do tempo para planejar sua visita à Ilha do Mel.',
  provider: 'IBGE / Open-Meteo',
  trackCategory: 'utility_click',
});
